import React from 'react';
import { usePrefersReducedMotion } from '@leagueplatform/web-common';
import { UnitTypes } from '#constants/types.ts';
import {
  PointsIcon,
  ChallengeTokenIcon,
  RewardPointsIcon,
} from '@leagueplatform/rewards-common';
import { CloseButton } from '#components/close-button/close-button.view.tsx';
import { Badge } from '@leagueplatform/web-common-components';
import {
  Box,
  UtilityText,
  keyframes,
  styled,
} from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';
import { useConfigProperty } from '@leagueplatform/core';

interface ToastPointsProps {
  title?: string;
  points: number;
  unit: string;
  onlyPoints: boolean;
  onClose: Function;
}

const slideBackAnimation = keyframes({
  from: {
    transform: 'translateX(calc(-50px * (1 - 2 * var(--league-is-rtl))))',
    opacity: 0,
  },
  to: {
    transform: 'translateX(0px)',
    opacity: 1,
  },
});

const PointsWrapper = styled(Box, {
  display: 'flex',
  height: '28px',
  animationTimingFunction: 'ease-out',
  animationDuration: '0.45s',
  animationDelay: '0.45s',
  animationFillMode: 'forwards',

  variants: {
    animationKey: {
      reduceMotion: {
        opacity: 1,
        animationName: 'none',
      },
      slideBack: {
        opacity: 0,
        animationName: `${slideBackAnimation}`,
      },
    },
  },
});

export const ToastPoints = ({
  onlyPoints,
  title,
  points,
  onClose,
  unit,
}: ToastPointsProps) => {
  const preferredReducedMotion = usePrefersReducedMotion();
  const { formatMessage } = useIntl();
  const showCompletedSuffix = useConfigProperty(
    'rewardsEvents.showCompletedSuffixInToast',
    false,
  );

  const displayTitle =
    showCompletedSuffix && title
      ? formatMessage({ id: 'TITLE_COMPLETED' }, { title })
      : title;

  return (
    <Box
      className="toast-wrapper"
      css={{
        display: 'flex',
        borderRadius: onlyPoints ? '$large' : '$large $large $none $none',
        backgroundColor: '$successBackgroundSubdued',
        width: '330px',
        minHeight: '48px',
        boxShadow: '$dropdown',
        padding: '$half $one',
      }}
    >
      <Box
        css={{
          display: 'flex',
          gap: '$half',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          flexGrow: 1,
        }}
      >
        <UtilityText
          size="sm"
          emphasis="emphasized"
          css={{ color: '$onSurfaceTextPrimary' }}
        >
          {displayTitle}
        </UtilityText>
        {Boolean(points) && (
          <PointsWrapper
            animationKey={preferredReducedMotion ? 'reduceMotion' : 'slideBack'}
            css={{ animationFillMode: 'forwards' }}
          >
            <Badge css={{ backgroundColor: '$successBackgroundSecondary' }}>
              {unit === UnitTypes.LEAGUE_POINTS && (
                <PointsIcon value={points} />
              )}
              {unit === UnitTypes.TOKENS && (
                <ChallengeTokenIcon value={points} />
              )}
              {unit === UnitTypes.REWARD_POINTS && (
                <RewardPointsIcon
                  value={points}
                  pointsText={formatMessage({ id: 'REWARD_POINTS' })}
                />
              )}
            </Badge>
          </PointsWrapper>
        )}
      </Box>
      <CloseButton onClose={onClose} />
    </Box>
  );
};
